import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { User } from 'firebase/auth';
import Logo from './logo';
import { FirebaseService } from '../lib/firebaseService';
import styles from '@/styles/modules/NavBar.module.css';
import { DropdownMenu } from './DropDownMenu';

interface NavBarProps {
  firebaseService: FirebaseService | null;
  isAuthenticated: boolean;
  currentUser: User | null;
  authLoading: boolean;
  firebaseInitialized: boolean;
}

export default function NavBar({
  firebaseService,
  isAuthenticated,
  currentUser,
  authLoading,
  firebaseInitialized 
}: NavBarProps): JSX.Element {
  const router = useRouter();
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>('inactive');
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);

  const checkSubscriptionStatus = useCallback(async (): Promise<void> => {
    if (!firebaseService || !currentUser?.uid || !firebaseInitialized) {
      // setSubscriptionLoading(false);
      return;
    }

    try {
      setSubscriptionLoading(true);
      console.log('🔍 [navbar.tsx] Checking subscription status for user:', currentUser.uid);

      // Get user document from Firestore
      const userDoc = await firebaseService.getUserDocument(currentUser.uid);

      if (userDoc && userDoc.subscriptionStatus) {
        const status = userDoc.subscriptionStatus;
        console.log('[navbar.tsx] 📋 User subscription status:', status);
        setSubscriptionStatus(status);

        // Redirect to pricing if subscription is not active
        if (status !== 'active') {
          console.log('[navbar.tsx] ❌ Subscription not active');
          // window.location.href = '/pricing';
          return;
        }

        console.log('[navbar.tsx] ✅ Subscription is active, proceeding to chat');
      } else {
        console.log('[navbar.tsx] ❌ No subscription status found');
        // window.location.href = '/pricing';
        return;
      }
    } catch (error) {
      console.error('[navbar.tsx] ❌ Error checking subscription status:', error);
      // On error, redirect to pricing for safety
      // window.location.href = '/pricing';
      return;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [firebaseService, currentUser?.uid, firebaseInitialized]);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  const handleSignOut = async () => {
    if (!firebaseService) {
      console.error('Firebase service not available');
      return;
    }

    try {
      await firebaseService.signOutUser();
      setMobileMenuOpen(false);
      router.push('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignIn = () => {
    setMobileMenuOpen(false);
    router.push('/signin');
  };

  const handleSignUp = () => {
    setMobileMenuOpen(false);
    router.push('/signup');
  };

  const handleChatClick = (): void => {
    if (subscriptionLoading) {
      console.log('⏳ Still checking subscription status, please wait...');
      return;
    }
    setMobileMenuOpen(false);
    if (isAuthenticated && subscriptionStatus === 'active' && currentUser) {
      console.log('✅ Subscription is active, proceeding to dashboard');
      router.push('/dashboard');
    } else if (!isAuthenticated && subscriptionStatus === 'inactive' && !currentUser) {
      router.push('/signup');
    } else if (isAuthenticated && subscriptionStatus !== 'active') {
      router.push('/pricing');
    } else {
      router.push('/signin');
    }
  };

  useEffect(() => {
    const handleScroll = (): void => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [router.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);

  const excludedPaths = ['/signin', '/signup', '/forgot-password'];
  const showAuthButtons = !excludedPaths.some(path => router.pathname.startsWith(path));

  const navLinks = [
    { href: '/', label: 'Home', icon: '🏠' },
    { href: '/docs', label: 'Docs', icon: '📚' },
    { href: '/pricing', label: 'Pricing', icon: '💰' },
    { href: '/about', label: 'About', icon: 'ℹ️' },
  ];

  const protectedLinks = [
    { href: '/chat', label: 'Chat', icon: '💬', priority: true },
    { href: '/conversations', label: 'History', icon: '📜', priority: true },
  ];

  return (
    <>
      <nav className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''}`}>
        <button
          className={styles.mobileMenuButton}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <div className={`${styles.menuIcon} ${mobileMenuOpen ? styles.open : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <Logo size="large" />

        <div className={styles.desktopNav}>
          {showAuthButtons && (
            <div className={styles.navButtons}>
              {authLoading ? (
                <button className={styles.signIn} disabled>Loading...</button>
              ) : isAuthenticated ? (
                <DropdownMenu
                  currentUser={currentUser}
                  onSignOut={handleSignOut}
                  onChatClick={() => {
                    handleChatClick();
                  }}
                />
              ) : (
                <>
                  <button className={styles.signIn} onClick={handleSignUp}>Sign Up</button>
                  <button className={styles.signIn} onClick={handleSignIn}>Sign In</button>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className={`${styles.mobileMenuOverlay}`} onClick={() => setMobileMenuOpen(false)}>
          <div className={`${styles.mobileMenu} ${styles.leftSlide}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.mobileMenuHeader}>
              <Logo size="small" />
              <button
                className={`${styles.mobileMenuClose}`}
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <div className={styles.mobileMenuLinks}>
              {isAuthenticated && (
                <>
                  <div className={`${styles.mobileNavSection} ${styles.prioritySection}`}>
                    <div className={styles.mobileNavSectionTitle}>AI Assistant</div>
                    {protectedLinks.map(link => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={router.pathname === link.href ? `${styles.active} ${styles.priorityLink}` : styles.priorityLink}
                      >
                        {link.icon} {link.label}
                      </Link>
                    ))}
                  </div>
                  <div className={`${styles.mobileNavDivider}`}></div>
                </>
              )}

              {navLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={router.pathname === link.href ? `${styles.active}` : ''}
                >
                  {link.icon} {link.label}
                </Link>
              ))}

              {showAuthButtons && (
                <>
                  <div className={styles.mobileNavDivider}></div>
                  {authLoading ? (
                    <button className={styles.mobileSignIn} disabled>Loading...</button>
                  ) : isAuthenticated ? (
                    <button className={`${styles.mobileSignIn} ${styles.logout}`} onClick={handleSignOut}>
                      👋 Log Out
                    </button>
                  ) : (
                    <>
                      <button className={styles.mobileSignIn} onClick={handleSignUp}>
                        📝 Sign Up
                      </button>
                      <button className={styles.mobileSignIn} onClick={handleSignIn}>
                        🔐 Sign In
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}